import { useEffect, useState } from "react";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Btn } from "../components/Btn";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { ProductCard } from "../components/ProductCard";
import { SectionHeader } from "../components/SectionHeader";
import { useCategories } from "../hooks/useCategories";
import { useBanners } from "../hooks/useBanners";
import { useProducts } from "../hooks/useProducts";
import { useStores } from "../hooks/useStores";
import { gradientForId } from "../lib/gradients";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SECTION_LIMIT = 12;

function todayLabel() {
  const d = new Date();
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function Discover({ user, setActive, onVisitStore, onOpenProduct, onOpenSearch }) {
  const { categories } = useCategories();
  const { banners } = useBanners();
  const { stores, loading: storesLoading } = useStores({ limit: 10 });
  const [cat, setCat] = useState("all");
  const firstName = user?.name?.split(" ")[0] || "there";
  const activeBanner = banners[0];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 14, marginBottom: 16 }}>
        <Card
          pad={0}
          style={{
            overflow: "hidden",
            background: activeBanner?.bg_color || `linear-gradient(135deg, ${C.hunter} 0%, ${C.hunterDk} 100%)`,
            color: C.dust, border: "none", display: "flex",
          }}
        >
          <div style={{ padding: "40px 34px", position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
            <div style={{ position: "absolute", right: -60, top: -60, width: 260, height: 260, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
            <div style={{ position: "absolute", left: -50, bottom: -50, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
            <div style={{ position: "relative", maxWidth: 480, width: "100%" }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, opacity: 0.75 }}>
                DISCOVER · {todayLabel().toUpperCase()}
              </div>
              <h1 style={{ fontSize: 40, fontWeight: 900, letterSpacing: "-1px", margin: "10px 0 4px", color: C.white, lineHeight: 1.05 }}>
                {activeBanner?.title || `${greeting()},`}
                <br />
                {activeBanner?.subtitle || `${firstName} 👋`}
              </h1>
              <p style={{ fontSize: 14, margin: "0 0 22px", opacity: 0.75 }}>
                Discover items from sellers near you.
              </p>
              <button
                onClick={() => onOpenSearch?.({ source: "search", query: "" })}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 18px", borderRadius: 14,
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: C.dust, textAlign: "left", cursor: "pointer", fontFamily: "inherit",
                }}
              >
                <Icon name="search" size={17} color={C.dust} />
                <span style={{ flex: 1, fontSize: 14, opacity: 0.85 }}>Search items, sellers, categories…</span>
              </button>
            </div>
          </div>
        </Card>

        <StoreSlideshow
          stores={stores}
          loading={storesLoading}
          onBrowse={() => setActive("stores")}
          onVisit={onVisitStore}
        />
      </div>

      <div className="no-scrollbar" style={{ display: "flex", gap: 8, marginBottom: 22, overflowX: "auto", paddingBottom: 4 }}>
        {categories.map((c) => {
          const on = cat === c.id;
          return (
            <button key={c.id} onClick={() => setCat(c.id)}
              style={{
                padding: "10px 16px", borderRadius: 14,
                border: `1.5px solid ${on ? C.hunter : "rgba(196,193,183,0.4)"}`,
                background: on ? C.hunter : C.white, color: on ? C.dust : C.slate,
                fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 8,
                whiteSpace: "nowrap", fontFamily: "inherit",
              }}>
              <span style={{ fontSize: 15 }}>{c.icon || "📦"}</span>
              {c.name}
            </button>
          );
        })}
      </div>

      <Section eyebrow="Just dropped"   title="Today's fresh picks" source="fresh"    cat={cat} onSeeAll={onOpenSearch} onOpenProduct={onOpenProduct} user={user} />
      <Section eyebrow="Hot this week"  title="Trending now"        source="trending" cat={cat} onSeeAll={onOpenSearch} onOpenProduct={onOpenProduct} user={user} />
      <Section eyebrow="Picked for you" title="For you"             source="for-you"  cat={cat} onSeeAll={onOpenSearch} onOpenProduct={onOpenProduct} user={user} hideWhenLoggedOut />
    </div>
  );
}

function Section({ eyebrow, title, source, cat, onSeeAll, onOpenProduct, user, hideWhenLoggedOut }) {
  const { products, loading } = useProducts({ source, categoryId: cat, limit: SECTION_LIMIT, userId: user?.id || null });

  if (hideWhenLoggedOut && !user?.id) return null;

  return (
    <div style={{ marginTop: 30 }}>
      <SectionHeader
        eyebrow={eyebrow}
        title={title}
        count={loading ? undefined : products.length}
        onSeeAll={() => onSeeAll?.({ source, title, categoryId: cat })}
      />
      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i}>
              <Skeleton h={220} r={16} />
              <div style={{ padding: "10px 4px" }}>
                <Skeleton h={14} style={{ marginBottom: 6 }} />
                <Skeleton h={10} w="60%" />
              </div>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          icon="package"
          title={source === "trending" ? "Nothing trending yet" : source === "for-you" ? "Shop to see picks for you" : "No items in this category"}
          body="Try a different category or check back later."
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {products.map((p) => (<ProductCard key={p.id} product={p} onOpen={onOpenProduct} />))}
        </div>
      )}
    </div>
  );
}

function StoreSlideshow({ stores, loading, onBrowse, onVisit }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (stores.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % stores.length), 5000);
    return () => clearInterval(t);
  }, [stores.length]);

  useEffect(() => {
    if (idx >= stores.length) setIdx(0);
  }, [stores.length, idx]);

  if (loading) {
    return (
      <Card pad={0} style={{ overflow: "hidden" }}>
        <Skeleton h={340} r={0} />
      </Card>
    );
  }

  if (stores.length === 0) {
    return (
      <Card pad={0} style={{ overflow: "hidden", minHeight: 340 }}>
        <EmptyState icon="store" title="No stores yet" body="Featured stores will appear here." />
      </Card>
    );
  }

  const s = stores[idx];
  const [g1, g2] = gradientForId(s.id);

  return (
    <Card pad={0} style={{ overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(196,193,183,0.25)" }}>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 800, color: C.muted, letterSpacing: 1, textTransform: "uppercase" }}>Featured stores</div>
          <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>Shops near you</h3>
        </div>
        <Btn kind="outline" size="sm" icon="store" onClick={onBrowse}>Browse all</Btn>
      </div>
      <div key={s.id} className="slideIn"
        style={{ position: "relative", height: 290, background: s.cover_url ? `url(${s.cover_url}) center/cover` : `linear-gradient(135deg, ${g1}, ${g2})`, overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 22 }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 60%)" }} />
        <div style={{ position: "relative", color: C.white }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, opacity: 0.85, marginBottom: 6 }}>
            STORE · {(s.category || "KAPIO").toUpperCase()}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: s.logo_url ? `url(${s.logo_url}) center/cover` : "rgba(255,255,255,0.95)", color: C.hunter, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 900 }}>
              {!s.logo_url && s.store_name?.[0]?.toUpperCase()}
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 900, margin: 0, letterSpacing: "-0.5px", color: C.white }}>
              {s.store_name}
            </h2>
          </div>
          {s.category && (<p style={{ fontSize: 13, margin: "4px 0 10px", opacity: 0.9 }}>{s.category}</p>)}
          <div style={{ display: "flex", gap: 14, fontSize: 11.5, fontWeight: 700, marginBottom: 12 }}>
            <span>⭐ {(s.rating_avg || 0).toFixed(1)} <span style={{ opacity: 0.7 }}>({s.rating_count || 0})</span></span>
            <span>·</span>
            <span>{s.product_count || 0} products</span>
            {s.total_orders > 0 && (<><span>·</span><span>{s.total_orders} orders</span></>)}
          </div>
          <Btn kind="dust" size="sm" icon="arrow-right" onClick={() => onVisit?.(s)}>Visit store</Btn>
        </div>
      </div>
      <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", background: C.white }}>
        <div style={{ display: "flex", gap: 6 }}>
          {stores.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)}
              style={{ width: i === idx ? 18 : 7, height: 7, borderRadius: 4, border: "none", background: i === idx ? C.hunter : C.dustDk, cursor: "pointer", transition: "width 0.2s" }} />
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setIdx((idx - 1 + stores.length) % stores.length)}
            style={{ width: 30, height: 30, border: "1px solid rgba(196,193,183,0.4)", borderRadius: 8, background: C.white, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="chevron-left" size={14} color={C.charcoal} />
          </button>
          <button onClick={() => setIdx((idx + 1) % stores.length)}
            style={{ width: 30, height: 30, border: "1px solid rgba(196,193,183,0.4)", borderRadius: 8, background: C.white, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="chevron-right" size={14} color={C.charcoal} />
          </button>
        </div>
      </div>
    </Card>
  );
}
